import type { HTMLAttributeAnchorTarget} from 'react';
import { useEffect, useMemo, useState } from 'react';
import Countdown from 'react-countdown';

import classNames from 'classnames';

import type { ModalProps } from '@uikit';
import { Modal, ModalCloseButton, ModalContent } from '@uikit';

import { MEMBERSHIP_TIER_ID } from '@api';

import type { HotOffer } from '@shared/api/types/hotOffers';
import NextLink from '@shared/common/components/NextLink';
import { membershipTiers } from '@shared/constants/membershipTiers';

const EarlyAccess = ({ accessErrorTime }: { accessErrorTime: number }) => {
  const [countdownDate, setCountdownDate] = useState<number>(0);
  useEffect(() => {
    setCountdownDate(new Date().getTime() + accessErrorTime);
  }, [accessErrorTime]);
  return (
    <>
      <h4 className="t-h3 text-center pb-4">This Offer Isn&apos;t Available for You Yet</h4>
      <p className="text-neutral-400 text-base text-center pb-4">
        This Hot Offer is presently exclusive to Premium Members. It will unlock for Standard
        Members in{' '}
        {countdownDate > 0 && (
          <div className="text-center inline">
            <Countdown
              date={countdownDate}
              daysInHours
              className="t-body1 font-semibold text-secondary"
            />
          </div>
        )}
        . Want immediate access? Upgrade your account now to claim this offer and enjoy other
        premium benefits!
      </p>
    </>
  );
};

const FullAccess = ({ accessTier }: Pick<HotOffer['attributes'], 'accessTier'>) => {
  const exclusiveTiersLabel = useMemo(() => {
    const tiersInOrder = [
      MEMBERSHIP_TIER_ID.FREE,
      MEMBERSHIP_TIER_ID.PLUS,
      MEMBERSHIP_TIER_ID.PREMIUM,
      MEMBERSHIP_TIER_ID.PREMIUM_PLUS,
    ];

    let result = '';

    for (let i = tiersInOrder.indexOf(accessTier); i < tiersInOrder.length; ++i) {
      const currentTier = tiersInOrder[i];

      if (!result) {
        result += membershipTiers[currentTier].name;
      } else {
        const separator = i === tiersInOrder.length - 1 ? ' and ' : ', ';
        result += `${separator}${membershipTiers[currentTier].name}`;
      }
    }

    return result;
  }, [accessTier]);

  return (
    <p className="text-center text-base font-normal">
      This offer is exclusive to {exclusiveTiersLabel} members only
    </p>
  );
};

interface AccessModalProps
  extends Pick<HotOffer['attributes'], 'accessTier'>,
    Omit<ModalProps, 'children'> {
  accessErrorTime: number;
  target?: HTMLAttributeAnchorTarget;
}

const AccessModal = ({
  accessTier,
  accessErrorTime,
  target = '_self',
  ...props
}: AccessModalProps) => {
  const Icon = membershipTiers?.[accessTier]?.icon;
  return (
    <Modal {...props}>
      <ModalContent
        className={classNames('p-0 max-w-[90%] overflow-hidden border-none', {
          'md:w-[560px]': !!accessErrorTime,
          'md:w-[460px]': !accessErrorTime,
        })}
      >
        <div
          style={{ background: membershipTiers[accessTier].gradient }}
          className="text-center t-h4 text-neutral-50"
        >
          <h2 className="py-4 flex justify-center items-center">
            {!!Icon && <Icon className="icon w-[20px] h-[20px] mr-1" />}
            {membershipTiers?.[accessTier]?.name}
          </h2>
        </div>
        <div className="px-12 pb-6">
          <div className="flex justify-end">
            <ModalCloseButton className="self-end !m-0" />
          </div>
          <div className="pb-8 pt-4">
            {!accessErrorTime && <FullAccess accessTier={accessTier} />}
            {!!accessErrorTime && <EarlyAccess accessErrorTime={accessErrorTime} />}
          </div>
          <NextLink
            href="/profile/membership"
            target={target}
            className="w-full button button-secondary button-lg"
          >
            Upgrade Account Now
          </NextLink>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AccessModal;
